import type React from "react"
import { cn } from "@/lib/utils"

// Base icon container that provides consistent styling
export const IndustrialIconContainer = ({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) => (
  <div
    className={cn(
      "relative w-48 h-48 flex items-center justify-center bg-gradient-to-br from-zinc-100 to-zinc-300 shadow-md transition-all duration-300 overflow-hidden",
      className,
    )}
  >
    {/* Diagonal stripes background pattern */}
    <div className="absolute inset-0 opacity-15">
      <div className="w-full h-full bg-stripes"></div>
    </div>

    {/* Highlight edge */}
    <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-red-500 to-red-700"></div>

    {/* Bottom accent */}
    <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-red-700 to-red-500"></div>

    {/* Icon content */}
    <div className="relative z-10">{children}</div>
  </div>
)

// Refrigerator Icon
export const IndustrialRefrigeratorIcon = ({ className }: { className?: string }) => (
  <IndustrialIconContainer className={cn("hexagon", className)}>
    <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" className="w-32 h-32">
      <defs>
        <linearGradient id="refrigerator-metal" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#DDD" />
          <stop offset="50%" stopColor="#FFF" />
          <stop offset="100%" stopColor="#BBB" />
        </linearGradient>
      </defs>

      {/* Main body */}
      <rect x="25" y="10" width="50" height="80" rx="3" fill="url(#refrigerator-metal)" stroke="#444" strokeWidth="2" />

      {/* Door divider */}
      <line x1="25" y1="40" x2="75" y2="40" stroke="#444" strokeWidth="3" />

      {/* Handles */}
      <rect x="70" y="20" width="3" height="15" rx="1" fill="#444" />
      <rect x="70" y="55" width="3" height="15" rx="1" fill="#444" />

      {/* Vents */}
      <rect x="30" y="15" width="20" height="2" rx="1" fill="#666" />
      <rect x="30" y="19" width="20" height="2" rx="1" fill="#666" />
      <rect x="30" y="23" width="20" height="2" rx="1" fill="#666" />

      {/* Ice/Water dispenser */}
      <rect x="55" y="20" width="10" height="15" rx="2" fill="#666" />
      <rect x="57" y="25" width="6" height="6" rx="1" fill="#444" />

      {/* Control panel */}
      <rect x="30" y="45" width="15" height="5" rx="1" fill="#444" />
      <circle cx="33" cy="47.5" r="1" fill="red" />
      <circle cx="37" cy="47.5" r="1" fill="#0088ff" />
      <circle cx="41" cy="47.5" r="1" fill="#00ff88" />

      {/* Texture lines */}
      <line x1="30" y1="55" x2="65" y2="55" stroke="#666" strokeWidth="0.5" strokeDasharray="1,1" />
      <line x1="30" y1="65" x2="65" y2="65" stroke="#666" strokeWidth="0.5" strokeDasharray="1,1" />
      <line x1="30" y1="75" x2="65" y2="75" stroke="#666" strokeWidth="0.5" strokeDasharray="1,1" />
    </svg>
  </IndustrialIconContainer>
)

// Washer & Dryer Icon
export const IndustrialWasherDryerIcon = ({ className }: { className?: string }) => (
  <IndustrialIconContainer className={cn("hexagon", className)}>
    <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" className="w-32 h-32">
      <defs>
        <linearGradient id="washer-metal" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#DDD" />
          <stop offset="50%" stopColor="#FFF" />
          <stop offset="100%" stopColor="#BBB" />
        </linearGradient>
        <linearGradient id="washer-glass" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#99CCFF" stopOpacity="0.4" />
          <stop offset="100%" stopColor="#3377FF" stopOpacity="0.4" />
        </linearGradient>
      </defs>

      {/* Washer */}
      <rect x="15" y="20" width="30" height="60" rx="2" fill="url(#washer-metal)" stroke="#333" strokeWidth="2" />
      <circle cx="30" cy="55" r="15" fill="url(#washer-glass)" stroke="#333" strokeWidth="1.5" />
      <circle cx="30" cy="55" r="10" fill="none" stroke="#555" strokeWidth="0.5" strokeDasharray="2,2" />
      <path d="M25 55 A5 5 0 0 1 35 55" stroke="#333" strokeWidth="1" fill="none" />
      <path d="M35 55 A5 5 0 0 1 25 55" stroke="#333" strokeWidth="1" fill="none" />

      {/* Washer control panel */}
      <rect x="15" y="20" width="30" height="10" rx="1" fill="#444" />
      <circle cx="20" cy="25" r="2" fill="#DDD" />
      <circle cx="27" cy="25" r="2" fill="#DDD" />
      <circle cx="34" cy="25" r="2" fill="#DDD" />
      <rect x="40" cy="23" width="3" height="4" rx="1" fill="#DDD" />

      {/* Dryer */}
      <rect x="55" y="20" width="30" height="60" rx="2" fill="url(#washer-metal)" stroke="#333" strokeWidth="2" />
      <circle cx="70" cy="55" r="15" fill="#555" stroke="#333" strokeWidth="1.5" />
      <circle cx="70" cy="55" r="10" fill="none" stroke="#777" strokeWidth="0.5" strokeDasharray="2,2" />
      <path d="M65 55 L75 55 M65 50 L75 50 M65 60 L75 60" stroke="#DDD" strokeWidth="1" />

      {/* Dryer control panel */}
      <rect x="55" y="20" width="30" height="10" rx="1" fill="#444" />
      <circle cx="60" cy="25" r="2" fill="#DDD" />
      <circle cx="67" cy="25" r="2" fill="#DDD" />
      <circle cx="74" cy="25" r="2" fill="#DDD" />
      <rect x="80" cy="23" width="3" height="4" rx="1" fill="#DDD" />

      {/* Vents */}
      <rect x="60" y="75" width="20" height="2" rx="1" fill="#333" />
      <rect x="60" y="72" width="20" height="2" rx="1" fill="#333" />
      <rect x="60" y="69" width="20" height="2" rx="1" fill="#333" />
    </svg>
  </IndustrialIconContainer>
)

// Oven & Range Icon
export const IndustrialOvenRangeIcon = ({ className }: { className?: string }) => (
  <IndustrialIconContainer className={cn("hexagon", className)}>
    <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" className="w-32 h-32">
      <defs>
        <linearGradient id="oven-metal" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#DDD" />
          <stop offset="50%" stopColor="#FFF" />
          <stop offset="100%" stopColor="#BBB" />
        </linearGradient>
        <radialGradient id="burner" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
          <stop offset="0%" stopColor="#FF5500" />
          <stop offset="70%" stopColor="#DD2200" />
          <stop offset="100%" stopColor="#880000" />
        </radialGradient>
      </defs>

      {/* Main body */}
      <rect x="20" y="20" width="60" height="60" rx="2" fill="url(#oven-metal)" stroke="#333" strokeWidth="2" />

      {/* Cooktop */}
      <rect x="20" y="20" width="60" height="20" rx="2" fill="#444" />

      {/* Burners */}
      <circle cx="30" cy="30" r="5" fill="#555" stroke="#222" strokeWidth="1" />
      <circle cx="30" cy="30" r="3" fill="url(#burner)" />

      <circle cx="50" cy="30" r="5" fill="#555" stroke="#222" strokeWidth="1" />
      <circle cx="50" cy="30" r="3" fill="url(#burner)" />

      <circle cx="70" cy="30" r="5" fill="#555" stroke="#222" strokeWidth="1" />
      <circle cx="70" cy="30" r="3" fill="url(#burner)" />

      {/* Oven door */}
      <rect x="25" y="45" width="50" height="30" rx="1" fill="#333" />
      <rect x="30" y="50" width="40" height="20" rx="1" fill="#222" stroke="#444" strokeWidth="1" />

      {/* Door handle */}
      <rect x="45" y="75" width="10" height="3" rx="1" fill="#555" />

      {/* Control panel */}
      <rect x="25" y="40" width="50" height="5" rx="1" fill="#555" />
      <circle cx="30" cy="42.5" r="1.5" fill="#DDD" />
      <circle cx="40" cy="42.5" r="1.5" fill="#DDD" />
      <circle cx="50" cy="42.5" r="1.5" fill="#DDD" />
      <circle cx="60" cy="42.5" r="1.5" fill="#DDD" />
      <circle cx="70" cy="42.5" r="1.5" fill="#DDD" />
    </svg>
  </IndustrialIconContainer>
)

// Dishwasher Icon
export const IndustrialDishwasherIcon = ({ className }: { className?: string }) => (
  <IndustrialIconContainer className={cn("hexagon", className)}>
    <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" className="w-32 h-32">
      <defs>
        <linearGradient id="dishwasher-metal" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#DDD" />
          <stop offset="50%" stopColor="#FFF" />
          <stop offset="100%" stopColor="#BBB" />
        </linearGradient>
      </defs>

      {/* Main body */}
      <rect x="25" y="15" width="50" height="70" rx="2" fill="url(#dishwasher-metal)" stroke="#333" strokeWidth="2" />

      {/* Control panel */}
      <rect x="25" y="15" width="50" height="15" rx="2" fill="#444" />

      {/* Buttons and display */}
      <rect x="30" y="20" width="15" height="5" rx="1" fill="#222" />
      <circle cx="35" cy="22.5" r="1" fill="#00ff00" />
      <circle cx="40" cy="22.5" r="1" fill="red" />

      <rect x="50" y="20" width="20" height="5" rx="1" fill="#222" />
      <rect x="52" y="21" width="16" height="3" rx="0.5" fill="#444" />
      <rect x="54" y="22" width="5" height="1" fill="#00ff88" />

      {/* Door */}
      <line x1="25" y1="30" x2="75" y2="30" stroke="#555" strokeWidth="2" />
      <line x1="25" y1="75" x2="75" y2="75" stroke="#555" strokeWidth="1" />

      {/* Racks */}
      <line x1="30" y1="40" x2="70" y2="40" stroke="#555" strokeWidth="1" strokeDasharray="2,1" />
      <line x1="30" y1="55" x2="70" y2="55" stroke="#555" strokeWidth="1" strokeDasharray="2,1" />

      {/* Dishes (simplified) */}
      <path d="M35 40 L35 55" stroke="#777" strokeWidth="1" />
      <path d="M45 40 L45 55" stroke="#777" strokeWidth="1" />
      <path d="M55 40 L55 55" stroke="#777" strokeWidth="1" />
      <path d="M65 40 L65 55" stroke="#777" strokeWidth="1" />

      {/* Water jets */}
      <circle cx="50" cy="47" r="1" fill="#99CCFF" />
      <path d="M50 47 L45 42" stroke="#99CCFF" strokeWidth="0.5" />
      <path d="M50 47 L55 42" stroke="#99CCFF" strokeWidth="0.5" />
      <path d="M50 47 L45 52" stroke="#99CCFF" strokeWidth="0.5" />
      <path d="M50 47 L55 52" stroke="#99CCFF" strokeWidth="0.5" />

      {/* Handle */}
      <rect x="45" y="65" width="10" height="3" rx="1" fill="#555" />
    </svg>
  </IndustrialIconContainer>
)

// Microwave Icon
export const IndustrialMicrowaveIcon = ({ className }: { className?: string }) => (
  <IndustrialIconContainer className={cn("hexagon", className)}>
    <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" className="w-32 h-32">
      <defs>
        <linearGradient id="microwave-metal" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#DDD" />
          <stop offset="50%" stopColor="#FFF" />
          <stop offset="100%" stopColor="#BBB" />
        </linearGradient>
      </defs>

      {/* Main body */}
      <rect x="20" y="30" width="60" height="40" rx="2" fill="url(#microwave-metal)" stroke="#333" strokeWidth="2" />

      {/* Door */}
      <rect x="25" y="35" width="30" height="30" rx="1" fill="#333" stroke="#555" strokeWidth="1" />
      <rect x="27" y="37" width="26" height="26" rx="1" fill="#222" stroke="#444" strokeWidth="0.5" />

      {/* Door handle */}
      <rect x="25" y="50" width="3" height="10" rx="1" fill="#555" />

      {/* Control panel */}
      <rect x="60" y="35" width="15" height="30" rx="1" fill="#333" />

      {/* Display */}
      <rect x="62" y="38" width="11" height="5" rx="1" fill="#222" />
      <rect x="64" y="40" width="7" height="1" fill="#00ff88" />

      {/* Buttons */}
      <rect x="62" y="45" width="5" height="5" rx="1" fill="#222" />
      <rect x="68" y="45" width="5" height="5" rx="1" fill="#222" />
      <rect x="62" y="52" width="5" height="5" rx="1" fill="#222" />
      <rect x="68" y="52" width="5" height="5" rx="1" fill="#222" />
      <rect x="62" y="59" width="11" height="3" rx="1" fill="#222" />

      {/* Turntable */}
      <circle cx="40" cy="50" r="10" fill="#222" stroke="#444" strokeWidth="0.5" />
      <circle cx="40" cy="50" r="8" fill="#111" stroke="#333" strokeWidth="0.5" strokeDasharray="1,1" />
      <circle cx="40" cy="50" r="1" fill="#555" />
      <line x1="40" y1="42" x2="40" y2="58" stroke="#555" strokeWidth="0.5" />
      <line x1="32" y1="50" x2="48" y2="50" stroke="#555" strokeWidth="0.5" />

      {/* Microwave radiation (stylized) */}
      <path d="M45 45 Q50 50, 45 55" stroke="#FFCC00" strokeWidth="0.5" fill="none" />
      <path d="M47 43 Q54 50, 47 57" stroke="#FFCC00" strokeWidth="0.5" fill="none" />
      <path d="M49 41 Q58 50, 49 59" stroke="#FFCC00" strokeWidth="0.5" fill="none" />
    </svg>
  </IndustrialIconContainer>
)

// Other Appliances Icon
export const IndustrialOtherAppliancesIcon = ({ className }: { className?: string }) => (
  <IndustrialIconContainer className={cn("hexagon", className)}>
    <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" className="w-32 h-32">
      <defs>
        <linearGradient id="other-metal" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#DDD" />
          <stop offset="50%" stopColor="#FFF" />
          <stop offset="100%" stopColor="#BBB" />
        </linearGradient>
      </defs>

      {/* Blender */}
      <rect x="15" y="60" width="20" height="10" rx="1" fill="url(#other-metal)" stroke="#333" strokeWidth="1" />
      <path d="M20 60 L20 50 L30 50 L30 60" fill="url(#other-metal)" stroke="#333" strokeWidth="1" />
      <path d="M18 50 L32 50 L30 40 L20 40 Z" fill="#99CCFF" stroke="#333" strokeWidth="1" opacity="0.7" />
      <rect x="23" y="35" width="4" height="5" fill="url(#other-metal)" stroke="#333" strokeWidth="1" />
      <circle cx="25" cy="65" r="3" fill="#333" />
      <path d="M22 65 L28 65 M25 62 L25 68" stroke="#DDD" strokeWidth="0.5" />

      {/* Coffee Maker */}
      <rect x="40" y="60" width="20" height="15" rx="1" fill="url(#other-metal)" stroke="#333" strokeWidth="1" />
      <rect x="45" y="40" width="10" height="20" rx="1" fill="#444" stroke="#333" strokeWidth="1" />
      <rect x="47" y="35" width="6" height="5" rx="1" fill="url(#other-metal)" stroke="#333" strokeWidth="1" />
      <rect x="45" y="55" width="10" height="5" fill="#222" />
      <circle cx="50" cy="67" r="5" fill="#333" stroke="#222" strokeWidth="1" />
      <path d="M48 67 L52 67 M50 65 L50 69" stroke="#DDD" strokeWidth="0.5" />
      <path d="M45 45 C50 43, 50 47, 55 45" stroke="#99CCFF" strokeWidth="0.5" fill="none" />
      <path d="M45 50 C50 48, 50 52, 55 50" stroke="#99CCFF" strokeWidth="0.5" fill="none" />

      {/* Toaster */}
      <rect x="65" y="60" width="20" height="15" rx="2" fill="url(#other-metal)" stroke="#333" strokeWidth="1" />
      <rect x="67" y="50" width="16" height="10" rx="1" fill="#333" stroke="#222" strokeWidth="1" />
      <rect x="70" y="52" width="4" height="6" rx="1" fill="#FF3300" opacity="0.7" />
      <rect x="76" y="52" width="4" height="6" rx="1" fill="#FF3300" opacity="0.7" />
      <path d="M70 50 L70 45 M80 50 L80 45" stroke="url(#other-metal)" strokeWidth="2" />
      <rect x="70" y="65" width="10" height="5" rx="1" fill="#222" />
      <circle cx="75" cy="67.5" r="1.5" fill="#FF3300" />

      {/* Connecting elements - circuit board style */}
      <path d="M25 75 L75 75" stroke="#00ff88" strokeWidth="1" strokeDasharray="2,2" />
      <circle cx="25" cy="75" r="1" fill="#00ff88" />
      <circle cx="50" cy="75" r="1" fill="#00ff88" />
      <circle cx="75" cy="75" r="1" fill="#00ff88" />
      <path d="M25 75 L25 70 M50 75 L50 70 M75 75 L75 70" stroke="#00ff88" strokeWidth="1" />

      {/* Decorative circuit elements */}
      <path d="M15 30 L85 30" stroke="#00ff88" strokeWidth="1" strokeDasharray="3,3" />
      <path d="M15 30 C30 20, 70 40, 85 30" stroke="#00ff88" strokeWidth="1" fill="none" strokeDasharray="1,1" />
      <circle cx="15" cy="30" r="1" fill="#00ff88" />
      <circle cx="50" cy="30" r="1" fill="#00ff88" />
      <circle cx="85" cy="30" r="1" fill="#00ff88" />
    </svg>
  </IndustrialIconContainer>
)
